import { Typography, useMediaQuery, useTheme } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React from 'react';
import { useNavigate } from 'react-router';
import ChipListCell from 'v2/components/ChipListCell/ChipListCell';
import { ColumnHeaderWithTooltip } from 'v2/components/ColumnHeaderWithTooltip/ColumnHeaderWithTooltip';
import { dataGridIdSlots } from 'v2/components/DataGrid/dataGridIdSlots';
import { renderHeaderWithId } from 'v2/components/DataGrid/renderHeaderWithId';
import GroupAvatarCell from 'v2/components/GroupAvatarCell/GroupAvatarCell';
import StatusChip from 'v2/components/StatusChip/StatusChip';
import { TextCell } from 'v2/components/TextCell/TextCell';
import { WarningCell } from 'v2/components/Warning/Warning';
import { DATAGRID_DEFAULT_PAGE_SIZE, DATAGRID_PAGE_SIZE_OPTIONS, DATAGRID_ROW_HEIGHT } from 'v2/constants';
import { DeviceGroupRow } from 'v2/hooks/useDeviceGroups/useDeviceGroups';
import { getDeviceGroupPath } from 'v2/paths';

interface Props {
  rows: DeviceGroupRow[];
  loading?: boolean;
}

const TableWithData: React.FC<Props> = ({ rows, loading = false }) => {
  const { translation } = useTranslation();
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const headerWithId = renderHeaderWithId();

  const columns: GridColDef<DeviceGroupRow>[] = [
    {
      field: 'name',
      headerName: translation.name,
      flex: 1,
      sortable: true,
      renderHeader: headerWithId,
      renderCell: (params) => <TextCell variant="title">{params.row.name}</TextCell>
    },
    {
      field: 'doors',
      headerName: translation.devices,
      flex: 1,
      sortable: false,
      renderHeader: headerWithId,
      renderCell: (params) => (
        <ChipListCell items={params.row.doors} emptyState={<WarningCell label={translation.no_devices} />} />
      )
    },
    {
      field: 'userGroups',
      headerName: translation.user_groups,
      flex: 1,
      sortable: false,
      minWidth: 170,
      renderHeader: headerWithId,
      renderCell: (params) => (
        <GroupAvatarCell
          groups={params.row.userGroups.map((group) => ({ id: group.id, name: group.name }))}
          emptyState={<WarningCell label={translation.no_user_group_s__assigned} />}
        />
      )
    },
    {
      field: 'access',
      headerName: translation.access,
      sortable: true,
      width: 130,
      valueGetter: (_, row) => row.doors.length > 0 && row.userGroups.length > 0,
      renderHeader: () => (
        <ColumnHeaderWithTooltip
          id="header-access"
          label={translation.access}
          tooltip={<Typography variant="caption">{translation.device_group_access_column_tooltip}</Typography>}
        />
      ),
      renderCell: (params) => {
        const isComplete = params.row.doors.length > 0 && params.row.userGroups.length > 0;
        return (
          <StatusChip
            variant={isComplete ? 'complete' : 'incomplete'}
            label={isComplete ? translation.complete : translation.incomplete}
            iconOnly={!isMdUp}
          />
        );
      }
    }
  ];

  const columnVisibilityModel = {
    name: true,
    doors: isSmUp,
    userGroups: isSmUp,
    access: true
  };

  return (
    <DataGrid
      rows={rows}
      columns={columns}
      columnVisibilityModel={columnVisibilityModel}
      initialState={{
        sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
        pagination: { paginationModel: { pageSize: DATAGRID_DEFAULT_PAGE_SIZE } }
      }}
      autoHeight
      hideFooter={rows.length <= DATAGRID_DEFAULT_PAGE_SIZE}
      pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
      rowHeight={DATAGRID_ROW_HEIGHT}
      loading={loading}
      disableRowSelectionOnClick
      disableColumnMenu
      onRowClick={(params) => navigate(getDeviceGroupPath(params.row.id))}
      slots={dataGridIdSlots}
      slotProps={{
        loadingOverlay: {
          variant: 'skeleton',
          noRowsVariant: 'skeleton'
        }
      }}
    />
  );
};

export default TableWithData;
